/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2021 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2021 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { SxProps, Theme } from '@mui/material';
import { Box, Typography } from '@mui/material';
import type { StyleLike } from 'ol/style/Style';
import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PROJECTION, renderCounter } from '@opengeoweb/shared';
import type { mapTypes, layerTypes } from '@opengeoweb/store';
import {
  mapActions,
  uiActions,
  uiTypes,
  defaultLayers,
  getSingularDrawtoolDrawLayerId,
  mapSelectors,
  layerActions,
  genericActions,
} from '@opengeoweb/store';
import type { TileServerSettings } from '@opengeoweb/webmap';
import { LayerType, webmapUtils } from '@opengeoweb/webmap';
import {
  emptyGeoJSON,
  genericOpenLayersFeatureStyle,
} from '@opengeoweb/webmap-react';
import { ClockConnect } from '../ClockConnect';
import type { MapControlsProps } from '../MapViewConnect/OlMapViewConnect';
import OlMapViewConnect from '../MapViewConnect/OlMapViewConnect';
import { TimeSliderConnect, TIME_SLIDER_Z_INDEX } from '../TimeSliderConnect';

const titleStyle = (theme: Theme): SxProps<Theme> => ({
  position: 'absolute',
  padding: '5px',
  zIndex: 50,
  color: theme.palette.common.black,
  whiteSpace: 'nowrap',
  userSelect: 'none',
  textShadow:
    '1px 1px 2px  #ffffffE6, -1px 1px 2px #ffffffE6, -1px -1px 2px  #ffffffE6, 1px -1px 2px #ffffffE6',
});

export const defaultBbox = {
  srs: PROJECTION.EPSG_3857.value,
  bbox: {
    left: 58703.6377,
    bottom: 6408480.4514,
    right: 3967387.5161,
    top: 11520588.9031,
  },
};

export interface ConfigurableMapConnectProps {
  id?: string;
  dockedLayerManagerSize?: mapTypes.DockedLayerManagerSize;
  shouldAutoUpdate?: boolean;
  shouldAnimate?: boolean;
  shouldAutoFetch?: boolean | number;
  title?: string;
  layers: layerTypes.Layer[];
  tileServerSettings?: TileServerSettings;
  autoUpdateLayerId?: string;
  autoTimeStepLayerId?: string;
  initialBbox?: mapTypes.Bbox;
  srs?: string;
  dimensions?: mapTypes.Dimension[];
  animationPayload?: mapTypes.AnimationPayloadType;
  shouldShowZoomControls?: boolean;
  displayMapPin?: boolean;
  showTimeSlider?: boolean; // used for map preset action
  disableTimeSlider?: boolean; // used by multimap to disable timeslider completely
  toggleTimestepAuto?: boolean;
  displayTimeInMap?: boolean;
  displayLayerManagerAndLegendButtonInMap?: boolean;
  displayDimensionSelectButtonInMap?: boolean;
  multiLegend?: boolean;
  shouldShowLayerManager?: boolean;
  shouldShowDockedLayerManager?: boolean;
  isClockEnabled?: boolean;
  displayGetFeatureInfoButtonInMap?: boolean;
  shouldDisplayDrawControls?: boolean;
  displaySearchButtonInMap?: boolean;
  displayDataExplorerButtonOnMap?: boolean;
  children?: React.ReactNode;
  mapControls?: React.ReactNode;
  shouldDisablePrefetching?: boolean;
  featureStyle?: StyleLike;
  shouldDisplayMeasureToolButton?: boolean;
}

export const ConfigurableMapConnect: React.FC<ConfigurableMapConnectProps> = ({
  id,
  dockedLayerManagerSize,
  title,
  layers = [],
  dimensions = [],
  shouldAutoUpdate = false,
  shouldAnimate = false,
  shouldAutoFetch = true,
  initialBbox,
  srs: initialSrs,
  shouldShowZoomControls = true,
  displayMapPin = false,
  showTimeSlider = true,
  disableTimeSlider = false,
  displayTimeInMap = false,
  displayLayerManagerAndLegendButtonInMap = true,
  displayDimensionSelectButtonInMap = true,
  multiLegend = true,
  shouldShowLayerManager,
  shouldShowDockedLayerManager,
  isClockEnabled = true,
  displayGetFeatureInfoButtonInMap = false,
  shouldDisplayDrawControls = false,
  displaySearchButtonInMap = false,
  displayDataExplorerButtonOnMap = false,
  mapControls,
  children,
  shouldDisablePrefetching,
  tileServerSettings,
  featureStyle = genericOpenLayersFeatureStyle,
  shouldDisplayMeasureToolButton = false,
  ...props
}: ConfigurableMapConnectProps) => {
  const dispatch = useDispatch();
  const mapId = React.useRef(id || webmapUtils.generateMapId()).current;

  const { layers: customLayers, proj: customProjection } =
    useSelector(mapSelectors.getdefaultMapSettings) ?? {};

  const mapWindowRef = React.useRef(null);

  const bbox = useMemo(
    () => initialBbox ?? customProjection?.bbox ?? defaultBbox.bbox,
    [initialBbox, customProjection?.bbox],
  );
  const srs = useMemo(
    () => initialSrs ?? customProjection?.srs ?? defaultBbox.srs,
    [initialSrs, customProjection?.srs],
  );

  // add a stable primitive key derived from bbox + srs
  const bboxKey = React.useMemo(
    () => `${bbox.left}|${bbox.bottom}|${bbox.right}|${bbox.top}|${srs}`,
    [bbox.left, bbox.bottom, bbox.right, bbox.top, srs],
  );

  React.useEffect(() => {
    const addBaselayer = !layers?.find(
      (layer) => layer.layerType === LayerType.baseLayer,
    );
    const defaultBaseLayer =
      customLayers?.find((layer) => layer.layerType === LayerType.baseLayer) ??
      defaultLayers.baseLayerGrey;

    const addOverlayer = !layers?.find(
      (layer) => layer.layerType === LayerType.overLayer,
    );
    const defaultOverLayer =
      customLayers?.find((layer) => layer.layerType === LayerType.overLayer) ??
      defaultLayers.overLayer;

    const updatedLayers = [
      ...(layers || []),
      ...(addBaselayer ? [defaultBaseLayer] : []),
      ...(addOverlayer ? [defaultOverLayer] : []),
    ];

    const mapPreset = {
      layers: updatedLayers,
      proj: { bbox, srs },
      dimensions,
      shouldAutoUpdate,
      shouldAnimate,
      shouldShowZoomControls,
      displayMapPin,
      showTimeSlider,
      dockedLayerManagerSize,
      ...props,
    };

    const initialProps: mapTypes.MapPresetInitialProps = { mapPreset };

    dispatch(mapActions.setMapPreset({ mapId, initialProps }));

    if (shouldShowLayerManager !== undefined) {
      dispatch(
        uiActions.setActiveMapIdForDialog({
          type: uiTypes.DialogTypes.LayerManager,
          mapId,
          setOpen: shouldShowLayerManager,
          source: 'app',
        }),
      );
    }
    if (shouldShowDockedLayerManager) {
      dispatch(
        uiActions.setToggleOpenDialog({
          type: uiTypes.DialogTypes.LayerManager,
          mapId,
          setOpen: false,
        }),
      );
      dispatch(
        uiActions.setToggleOpenDialog({
          type: `${uiTypes.DialogTypes.DockedLayerManager}-${mapId}`,
          mapId,
          setOpen: true,
        }),
      );
    }
    if (shouldDisplayDrawControls) {
      dispatch(
        layerActions.addLayer({
          mapId,
          layer: {
            geojson: emptyGeoJSON,
            layerType: LayerType.featureLayer,
            isSelectable: false,
            isInEditMode: false,
          },
          layerId: getSingularDrawtoolDrawLayerId(mapId),
          origin: 'ConfigurableMapConnect',
        }),
      );
    }
    dispatch(
      genericActions.setBbox({
        bbox,
        sourceId: mapId,
        srs,
      }),
    );
    // only re-run when bbox values (or srs) actually change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bboxKey]);

  const mapControlConfig = useMemo(
    (): MapControlsProps => ({
      mapControlsPositionTop: title ? 24 : 8,
      search: displaySearchButtonInMap,
      zoomControls: shouldShowZoomControls,
      layerManagerAndLegend: displayLayerManagerAndLegendButtonInMap,
      multiLegend,
      dimensionSelect: displayDimensionSelectButtonInMap,
      getFeatureInfo: displayGetFeatureInfoButtonInMap,
      dataExplorerButton: displayDataExplorerButtonOnMap,
      measureToolButton: shouldDisplayMeasureToolButton,
      additionalMapControls: mapControls,
    }),
    [
      title,
      displaySearchButtonInMap,
      shouldShowZoomControls,
      displayLayerManagerAndLegendButtonInMap,
      multiLegend,
      displayDimensionSelectButtonInMap,
      displayGetFeatureInfoButtonInMap,
      displayDataExplorerButtonOnMap,
      shouldDisplayMeasureToolButton,
      mapControls,
    ],
  );

  return React.useMemo(() => {
    renderCounter.count(`${ConfigurableMapConnect.name}_${mapId}`);
    return (
      <Box
        data-testid="ConfigurableMap"
        ref={mapWindowRef}
        sx={{
          width: '100%',
          height: '100%',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {title ? (
          <Typography data-testid="mapTitle" sx={titleStyle as SxProps<Theme>}>
            {title}
          </Typography>
        ) : null}
        {!disableTimeSlider && (
          <Box
            sx={{
              position: 'absolute',
              left: '0px',
              bottom: '0px',
              zIndex: TIME_SLIDER_Z_INDEX,
              width: '100%',
            }}
          >
            <TimeSliderConnect
              mapId={mapId}
              mapWindowRef={mapWindowRef}
              sourceId={mapId}
            />
          </Box>
        )}

        <OlMapViewConnect
          controls={mapControlConfig}
          displayTimeInMap={displayTimeInMap}
          featureStyle={featureStyle}
          initialBbox={bbox}
          mapId={mapId}
          shouldAutoFetch={shouldAutoFetch}
          shouldDisablePrefetching={shouldDisablePrefetching}
          showScaleBar={false}
          tileServerSettings={tileServerSettings}
        >
          {children}
        </OlMapViewConnect>

        {isClockEnabled ? <ClockConnect mapId={mapId} /> : null}
      </Box>
    );
    // don't include bbox directly, use bboxKey instead
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mapId,
    title,
    bboxKey,
    mapControlConfig,
    tileServerSettings,
    disableTimeSlider,
    displayTimeInMap,
    shouldAutoFetch,
    shouldDisablePrefetching,
    children,
    isClockEnabled,
    featureStyle,
  ]);
};
